import { useTranslation } from 'react-i18next';
import RadioGroupField from '../../../../../components/fields/RadioGroupField';
import CollapsibleWrapper, { Panel } from '../../../../../components/wrappers/CollapsibleWrapper';
import useFinanceProductOptions from '../../shared/useFinanceProductOptions';
import useVisible from '../../shared/useVisible';
import AverageMileage from './AverageMileage';
import Balloon from './Balloon';
import BalloonGfv from './BalloonGfv';
import Commission from './Commission';
import Displacement from './Displacement';
import DownPaymentOrLoan from './DownPaymentOrLoan';
import InsuranceFee from './InsuranceFee';
import InterestRate from './InterestRate';
import LeasePurchaseResidualValue from './LeasePurchaseResidualValue';
import LicenseAndFuelTax from './LicenseAndFuelTax';
import LicensePlateFee from './LicensePlateFee';
import MonthlyPaymentFixedInterestRate from './MonthlyPaymentFixedInterestRate';
import SecurityDeposit from './SecurityDeposit';
import TaxLoss from './TaxLoss';
import Term from './Term';

type ConfigurationProps = {
    disabled: boolean;
};
export const FinanceProductBasedOnField = ({ disabled }: ConfigurationProps) => {
    const { t } = useTranslation(['financeProductDetails']);

    const { financeProductBasedOnOptions } = useFinanceProductOptions();

    return (
        <RadioGroupField
            {...t('financeProductDetails:fields.configureBasedOn', {
                returnObjects: true,
            })}
            disabled={disabled}
            name="basedOn"
            options={financeProductBasedOnOptions}
            required
        />
    );
};

const ParametersForm = ({ disabled = false }: { disabled: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);

    const visible = useVisible();

    return (
        <CollapsibleWrapper defaultActiveKey={Object.keys(visible)}>
            {visible.downPayment && (
                <Panel key="downPayment" header={t('financeProductDetails:subTitles.downPaymentOrLoan')}>
                    <DownPaymentOrLoan disabled={disabled} />
                </Panel>
            )}
            {visible.term && (
                <Panel key="term" header={t('financeProductDetails:subTitles.paymentTerm')}>
                    <Term disabled={disabled} />
                </Panel>
            )}
            {visible.licensePlateFee && (
                <Panel key="licensePlateFee" header={t('financeProductDetails:subTitles.licensePlateFee')}>
                    <LicensePlateFee disabled={disabled} />
                </Panel>
            )}
            {visible.commission && (
                <Panel key="commission" header={t('financeProductDetails:subTitles.commission')}>
                    <Commission disabled={disabled} />
                </Panel>
            )}
            {visible.interestRate && (
                <Panel key="interestRate" header={t('financeProductDetails:subTitles.interestRate')}>
                    <InterestRate disabled={disabled} />
                </Panel>
            )}
            {visible.monthlyPaymentFixedInterestRate && (
                <Panel
                    key="monthlyPaymentFixedInterestRate"
                    header={t('financeProductDetails:subTitles.monthlyPaymentFixedRate')}
                >
                    <MonthlyPaymentFixedInterestRate disabled={disabled} />
                </Panel>
            )}
            {visible.licenseAndFuelTax && (
                <Panel key="licenseAndFuelTax" header={t('financeProductDetails:subTitles.licenseAndFuelTax')}>
                    <LicenseAndFuelTax disabled={disabled} />
                </Panel>
            )}
            {visible.displacement && (
                <Panel key="displacement" header={t('financeProductDetails:subTitles.displacement')}>
                    <Displacement disabled={disabled} />
                </Panel>
            )}
            {visible.insuranceFee && (
                <Panel key="insuranceFee" header={t('financeProductDetails:subTitles.insuranceFee')}>
                    <InsuranceFee disabled={disabled} />
                </Panel>
            )}
            {visible.taxLoss && (
                <Panel key="taxLoss" header={t('financeProductDetails:subTitles.taxLoss')}>
                    <TaxLoss disabled={disabled} />
                </Panel>
            )}
            {visible.deposit && (
                <Panel key="deposit" header={t('financeProductDetails:subTitles.securityDeposit')}>
                    <SecurityDeposit disabled={disabled} />
                </Panel>
            )}
            {visible.balloon && (
                <Panel key="balloon" header={t('financeProductDetails:subTitles.balloon')}>
                    <Balloon disabled={disabled} />
                </Panel>
            )}
            {visible.residualValue && (
                <>
                    <Panel
                        key="residualValue.averageMileage"
                        header={t('financeProductDetails:subTitles.averageMileage')}
                    >
                        <AverageMileage disabled={disabled} />
                    </Panel>
                    <Panel key="residualValue" header={t('financeProductDetails:subTitles.residualValue')}>
                        <LeasePurchaseResidualValue disabled={disabled} />
                    </Panel>
                </>
            )}
            {visible.balloonGfv && (
                <Panel key="balloonGfv" header={t('financeProductDetails:subTitles.balloon')}>
                    <BalloonGfv disabled={disabled} />
                </Panel>
            )}
        </CollapsibleWrapper>
    );
};

export default ParametersForm;
